import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../loader/Loader";
import { FileUploader } from "../../uploads/FileUploader";
import { useCreatePlanMutation } from "../../../features/api/plansApiSlice";

const AdminAddPlan = () => {
  const navigate = useNavigate();
  const [uploadedPlansUrls, setUploadedPlansUrls] = useState(null);
  const [createPlan, { isLoading }] = useCreatePlanMutation();

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    if (!uploadedPlansUrls) {
      toast.error("Please upload a pdf first.");
      setSubmitting(false);
      return;
    }

    try {
      const response = await createPlan({
        ...values,
        ...uploadedPlansUrls,
      }).unwrap();

      toast.success(response?.message || "Pdf entry created successfully!");
      resetForm();
      setUploadedPlansUrls(null);
      navigate("/admin/plans");
    } catch (error) {
      console.error("Failed to create plan entry:", error);
      toast.error(error?.data?.message || "Failed to create plan entry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  const validateData = Yup.object().shape({
    title: Yup.string().required("Plan title is required"),
    description: Yup.string().required("Plan description is required"),
    planType: Yup.string().required("Video type is required"),
  });

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Add Plan</h1>
          <button onClick={() => navigate("/admin/videos/add-video")}>
            Add Plan
          </button>
        </div>
      </div>
      <Formik
        initialValues={{
          title: "",
          description: "",
          planType: "",
        }}
        validationSchema={validateData}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form className="dashboard-form">
            <div>
              <label htmlFor="title">
                Title<span className="required-field">*</span>
              </label>
              <Field type="text" name="title" />
              <ErrorMessage name="title" component="div" className="error" />
            </div>

            <div className="form-controls">
              <label htmlFor="planType">
                Public or Private<span className="required-field">*</span>
              </label>
              <Field as="select" name="planType">
                <option value="">Select Plan type</option>
                <option value="paid">Paid</option>
                <option value="free">Free</option>
              </Field>
              <ErrorMessage name="planType" component="div" className="error" />
            </div>

            <div>
              <label>Upload File</label>
              <FileUploader
                identifier="pdf"
                uploadType="plan"
                initialUrls={{
                  pdfUrls: values?.pdfUrls || [],
                }}
                onUpload={(urls) => {
                  const pdfs = urls.pdfUrls || urls.resources || [];
                  setFieldValue("pdfUrls", pdfs);
                  setUploadedPlansUrls({ pdfUrls: pdfs }); // ✅ store in local state
                }}
              />
            </div>

            <div>
              <label htmlFor="description">
                Description<span className="required-field">*</span>
              </label>
              <ReactQuill
                className="custom-quill-editor"
                value={values.description || ""}
                onChange={(value) => setFieldValue("description", value)}
                name="description"
              />
              <ErrorMessage
                name="description"
                component="div"
                className="error"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="form-button"
            >
              Add A Plan
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AdminAddPlan;
